import { isArray } from '@qctsw/utils'
import type { BrandResponse, ModelResponse, SeriesItem, SeriesResponse } from '@/server/types'
import { brandListApi, modelListApi, seriesListApi } from '@/server'
import { LOAD_STATE } from '~/shared/types'

export interface CarSelected {
  brand: BrandResponse | null
  series: SeriesItem & Omit<SeriesResponse, 'seriesLs'> | null
  model: ModelResponse | null
  isInit?: boolean
}

export function useCarBrand() {
  const loadState = ref(LOAD_STATE.INIT)
  const brandKeys = ref<string[]>([])
  const brandList = reactive<Map<string, BrandResponse[]>>(new Map())
  async function getBrandList() {
    const res = await useLoadState({
      state: loadState,
      onlyClient: true,
      fn: () => brandListApi(),
    })
    res && formatBrand(res.data)
  }

  function formatBrand(data: Record<string, BrandResponse[]>) {
    for (const key in data) {
      const k = key.toLocaleUpperCase()
      const brands = data[key]
      if (!brandList.has(k)) {
        brandList.set(k, brands)
      }
      else {
        const list = brandList.get(k)
        if (list) {
          list.push(...brands)
          brandList.set(k, list)
        }
      }
    }
    brandKeys.value = Array.from(brandList.keys()).sort((a, b) => a.localeCompare(b))
  }

  async function init(brandId: number) {
    if (!brandId)
      return false
    await getBrandList()
    for (const [, brands] of brandList) {
      const brand = brands.find(item => item.id === brandId)
      if (brand)
        return brand
    }
    return false
  }

  return {
    loadState,
    brandKeys,
    brandList,
    getBrandList,
    init,
  }
}

export function useCarSeries() {
  const loadState = ref(LOAD_STATE.INIT)
  const seriesList = ref<SeriesResponse[]>([])
  async function getSeriesList(brandId: number) {
    const res = await useLoadState({
      state: loadState,
      onlyClient: true,
      fn: () => seriesListApi(brandId),
    })
    if (res)
      seriesList.value = format(res.data)
  }

  function format(data: SeriesResponse[]) {
    return data.map((item) => {
      return {
        ...item,
        seriesLs: item.seriesLs?.filter(series => series.carSeriesName).sort((a, b) => a.carSeriesName.localeCompare(b.carSeriesName)),
      }
    }).filter(item => item.seriesLs?.length)
  }
  async function init(id: number, brandId: number) {
    if (!brandId || !id)
      return false
    await getSeriesList(brandId)
    if (!isArray(seriesList.value) || !seriesList.value.length)
      return false
    for (const vendor of seriesList.value) {
      const series = vendor.seriesLs.find(item => item.id === id)
      if (series) {
        return {
          ...series,
          carVendorName: vendor.carVendorName,
          carVendorId: vendor.carVendorId,
          brandVendorId: vendor.brandVendorId,
        }
      }
    }
    return false
  }

  return {
    loadState,
    seriesList,
    getSeriesList,
    format,
    init,
  }
}

export function useCarModel() {
  const loadState = ref(LOAD_STATE.INIT)
  const yearList = ref<string[]>([])
  const modelList = reactive<Map<string, ModelResponse[]>>(new Map())

  async function getModelList(seriesId: number) {
    const res = await useLoadState({
      state: loadState,
      onlyClient: true,
      fn: () => modelListApi({ seriesId }),
    })
    if (res)
      format(res.data)
  }

  function format(data: Record<string, ModelResponse[]>) {
    yearList.value = []
    modelList.clear()
    for (const year in data) {
      if (!modelList.has(year)) {
        yearList.value.push(year)
        modelList.set(year, [])
      }
      modelList.get(year)?.push(...(data[year].filter(model => model.carModelName)
        .sort((a, b) => a.carModelName.localeCompare(b.carModelName))))
    }
    yearList.value.sort((a, b) => Number(b) - Number(a))
  }
  async function init(id: number, seriesId: number) {
    if (!seriesId || !id)
      return false
    await getModelList(seriesId)
    for (const [, models] of modelList) {
      const model = models.find(item => item.id === id)
      if (model)
        return model
    }
    return false
  }

  return {
    loadState,
    yearList,
    modelList,
    getModelList,
    format,
    init,
  }
}
